import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';

import { NextIcon, PreviousIcon } from 'honeydew/icons/Carousels';
import { getResizedCDNAsset } from 'utils/globalUtils';

import { usePrevNextButtons } from '../Carousels/shared/PrevNextButtons';

/**
 * A carousel component that displays a main image with thumbnail navigation.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{src: string, alt?: string}>} props.images - Array of image objects containing src and optional alt text
 * @returns {JSX.Element} A carousel component with main image and thumbnail navigation
 */
function ThumbnailCarousel({ images }) {
    const [selectedIndex, setSelectedIndex] = useState(0);

    const [emblaMainRef, emblaMainApi] = useEmblaCarousel();

    const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
        containScroll: 'keepSnaps',
        dragFree: true,
    });

    const { nextBtnDisabled, onNextButtonClick, onPrevButtonClick, prevBtnDisabled } = usePrevNextButtons(emblaMainApi);

    const onThumbClick = useCallback(
        (index) => {
            if (!emblaMainApi || !emblaThumbsApi) return;
            emblaMainApi.scrollTo(index);
        },
        [emblaMainApi, emblaThumbsApi]
    );

    const onSelect = useCallback(() => {
        if (!emblaMainApi || !emblaThumbsApi) return;
        setSelectedIndex(emblaMainApi.selectedScrollSnap());
        emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
    }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

    useEffect(() => {
        if (!emblaMainApi) return;
        onSelect();

        emblaMainApi.on('select', onSelect).on('reInit', onSelect);
    }, [emblaMainApi, onSelect]);

    return (
        <div>
            <div className="overflow-hidden" ref={images.length > 1 ? emblaMainRef : null}>
                <div className="flex touch-pan-y touch-pinch-zoom">
                    {images.map((image, index) => (
                        <div className="min-w-0 flex-[0_0_100%] transform-gpu" key={image.src}>
                            {/* 700px max display size (700px on mobile, 600px on desktop). 2x for high density displays */}
                            <img
                                src={getResizedCDNAsset({ url: image.src, width: 1400 })}
                                alt={image.alt || ''}
                                className="h-full w-full object-cover"
                                width={700}
                                height={700}
                                loading={index === 0 ? 'eager' : 'lazy'}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {images?.length > 1 && (
                <div className="ml-10 mt-10 md:ml-0">
                    <div className="relative">
                        {!prevBtnDisabled && (
                            <button
                                type="button"
                                onClick={onPrevButtonClick}
                                aria-label="Previous"
                                className="absolute left-0 top-1/2 z-10 hidden -translate-y-1/2 md:block"
                            >
                                <PreviousIcon />
                            </button>
                        )}
                        <div className="overflow-hidden" ref={emblaThumbsRef}>
                            <ul className="flex gap-5">
                                {images.map((image, index) => (
                                    <li
                                        key={image.src}
                                        className={clsx('shrink-0 rounded-[4px] border-2', {
                                            'border-kale if:border-beet': index === selectedIndex,
                                            'border-grey-light hover:border-black': index !== selectedIndex,
                                        })}
                                    >
                                        <button
                                            onClick={() => onThumbClick(index)}
                                            type="button"
                                            className="block p-[2px]"
                                        >
                                            <img
                                                src={getResizedCDNAsset({ url: image.src, width: 160 })}
                                                alt={image.alt || ''}
                                                className={`size-80 object-cover ${
                                                    index === selectedIndex ? 'opacity-100' : 'opacity-50'
                                                }`}
                                                width={80}
                                                height={80}
                                                loading="lazy"
                                            />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        {!nextBtnDisabled && (
                            <button
                                type="button"
                                onClick={onNextButtonClick}
                                aria-label="Next"
                                className="absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 md:block"
                            >
                                <NextIcon />
                            </button>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default ThumbnailCarousel;
